import type { CaptureLogOptions, LogAttributes } from "posthog-js";

import { sanitizeUrlsInString, stripMediaUrl } from "@/lib/strip-media-url";

const MEDIA_URL_KEYS = new Set(["file", "src", "url", "video_url"]);

const ERROR_PAYLOAD_KEYS = new Set([
	"config",
	"data",
	"headers",
	"request",
	"response",
	"stack",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
	value !== null && typeof value === "object" && !Array.isArray(value);

const isErrorLikeObject = (obj: Record<string, unknown>): boolean => {
	if (
		"stack" in obj ||
		"statusCode" in obj ||
		"response" in obj ||
		"data" in obj
	) {
		return true;
	}

	if (typeof obj.name !== "string") {
		return false;
	}

	return (
		obj.name === "ApiError" || obj.name === "AxiosError" || obj.name === "Error"
	);
};

const extractBalancedJsonObject = (
	text: string,
	startIndex: number,
): null | { end: number; json: string; start: number } => {
	if (text[startIndex] !== "{") {
		return null;
	}

	let depth = 0;
	let inString = false;
	let escape = false;

	for (let index = startIndex; index < text.length; index += 1) {
		const char = text[index];

		if (inString) {
			if (escape) {
				escape = false;
			} else if (char === "\\") {
				escape = true;
			} else if (char === '"') {
				inString = false;
			}
			continue;
		}

		if (char === '"') {
			inString = true;
			continue;
		}

		if (char === "{") {
			depth += 1;
		} else if (char === "}") {
			depth -= 1;
			if (depth === 0) {
				return {
					end: index + 1,
					json: text.slice(startIndex, index + 1),
					start: startIndex,
				};
			}
		}
	}

	return null;
};

const sanitizeContextObject = (
	obj: Record<string, unknown>,
): Record<string, unknown> => {
	const sanitized: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(obj)) {
		if (MEDIA_URL_KEYS.has(key) && typeof value === "string") {
			sanitized[key] = stripMediaUrl(value);
			continue;
		}

		if (key === "message" && typeof value === "string") {
			sanitized[key] = sanitizeUrlsInString(value);
			continue;
		}

		if (
			typeof value === "string" ||
			typeof value === "number" ||
			typeof value === "boolean"
		) {
			sanitized[key] =
				typeof value === "string" ? sanitizeUrlsInString(value) : value;
		}
	}

	return sanitized;
};

const sanitizeErrorObject = (
	obj: Record<string, unknown>,
): Record<string, unknown> => {
	const sanitized: Record<string, unknown> = {};

	if (typeof obj.name === "string") {
		sanitized.name = obj.name;
	}

	if (typeof obj.message === "string") {
		sanitized.message = sanitizeUrlsInString(obj.message);
	}

	if (typeof obj.statusCode === "number") {
		sanitized.statusCode = obj.statusCode;
	}

	for (const key of ERROR_PAYLOAD_KEYS) {
		if (key in obj && obj[key] !== undefined) {
			sanitized[key] = "[redacted]";
		}
	}

	return sanitized;
};

const sanitizeLogJsonObject = (
	obj: Record<string, unknown>,
): Record<string, unknown> =>
	isErrorLikeObject(obj)
		? sanitizeErrorObject(obj)
		: sanitizeContextObject(obj);

/** posthog-js puts console args in `body`; embedded `{...}` blobs need structural redaction. */
export const sanitizeLogBody = (body: string): string => {
	let result = body;
	let searchFrom = 0;

	while (searchFrom < result.length) {
		const braceIndex = result.indexOf("{", searchFrom);
		if (braceIndex === -1) {
			break;
		}

		const extracted = extractBalancedJsonObject(result, braceIndex);
		if (!extracted) {
			return sanitizeUrlsInString(`${result.slice(0, braceIndex)}[redacted]`);
		}

		try {
			const parsed: unknown = JSON.parse(extracted.json);
			if (!isRecord(parsed)) {
				searchFrom = braceIndex + 1;
				continue;
			}

			const replacement = JSON.stringify(sanitizeLogJsonObject(parsed));
			result =
				result.slice(0, extracted.start) +
				replacement +
				result.slice(extracted.end);
			searchFrom = extracted.start + replacement.length;
		} catch {
			return sanitizeUrlsInString(`${result.slice(0, braceIndex)}[redacted]`);
		}
	}

	return sanitizeUrlsInString(result);
};

const sanitizeLogAttributes = (attributes: LogAttributes): LogAttributes => {
	const sanitized: LogAttributes = {};

	for (const [key, value] of Object.entries(attributes)) {
		if (MEDIA_URL_KEYS.has(key) && typeof value === "string") {
			sanitized[key] = stripMediaUrl(value);
			continue;
		}

		if (typeof value === "string") {
			sanitized[key] = sanitizeUrlsInString(value);
			continue;
		}

		if (typeof value === "number" || typeof value === "boolean") {
			sanitized[key] = value;
		}
	}

	return sanitized;
};

/** Redact presigned URLs and API error payloads before console logs leave the browser. */
export const sanitizePostHogLogRecord = (
	record: CaptureLogOptions,
): CaptureLogOptions => {
	const sanitized: CaptureLogOptions = {
		...record,
		body: sanitizeLogBody(record.body),
	};

	if (record.attributes) {
		sanitized.attributes = sanitizeLogAttributes(record.attributes);
	}

	return sanitized;
};
